import type { ReactElement } from 'react';

import { useTheme } from '@emotion/react';

import { CheckMarkIcon } from '@twistopayments/design-system/icons/inline';

import { typographyVariant } from '../typography';

import * as styles from './password-strength.styles';

type Props<T extends Record<string, unknown> = Record<string, unknown>> = {
  rulesMap: { [P in keyof T]: boolean };
  rulesNodes: { [P in keyof T]: ReactElement | string };
};

export const PasswordStrengthRuleList = <T extends Record<string, unknown>>({
  rulesMap,
  rulesNodes,
}: Props<T>) => {
  const theme = useTheme();

  return (
    <ul css={styles.root} data-testid="password-strength-rule-list">
      {Object.keys(rulesNodes).map((nodeKey) => {
        const isChecked = rulesMap[nodeKey as keyof T];
        const check = (
          <CheckMarkIcon
            aria-label={isChecked ? 'password-matched' : 'password-not-matched'}
            color={isChecked ? 'secondaryMaxContrast' : 'inherit'}
            height="100%"
            width="100%"
          />
        );

        const node = rulesNodes[nodeKey as keyof T];

        return (
          <li key={nodeKey} aria-label={nodeKey} css={styles.listItem(theme)}>
            <div css={styles.listItemIcon(theme)}>{check}</div>
            <div
              css={[
                styles.listItemText(theme),
                typographyVariant(theme).b3,
                isChecked && styles.checkedListItemText(theme),
              ]}>
              {node}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
